import _ from "underscore";
import DriveView from "../../DriveView";
import { Device } from "../../model/Device";
import { dstCalculator } from "../../utils/date";
import DVIRLog from "../../model/DVIRLog";
import { TrailerAttachments } from "../../model/TrailerAttachments";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import { BaseCache, makeDeviceCache } from "../../utils/storage/cache";
import { getCurrentVehicle } from "../../utils/device";
import { getServerDate } from "../../utils/storage/date";
import dayjs from "../../utils/dayjs";
import { htmlEscape } from "../../utils/vanilla";
import { AuditType, auditSkip } from "../../utils/auditHelper";
import { Trailer } from "../../model/Trailer";
import { PageLevel } from "../../store/mobile/types";
import ButtonsBar from "../framework/buttonsBar";
import pageHTML from "./shared/main.tmpl";
import logTemplate from "./main-log.tmpl";
import { DriveStore } from "../../store/reducers";
import { IDefect } from "../../model/Defect";
import { getHiddenRoadsideCheckLanguageViewModel } from "../inspection/roadsideCheckLanguageSelector";

const viewInspectionRoute = (logId: string | undefined) => `dvir/inspect,(id:'${ logId }')`;
const newInspectionRoute = (assetId: string | undefined) => `dvir/inspect,(assetId:'${ assetId }')`;

interface IDvirAudit {
    name: string;
    id: string;
    assetType: string;
}

export default class DvirView extends DriveView<Backbone.Model> {

    protected strings = [
        "Skip remainder"
    ];
    private readonly template = _.template(pageHTML);
    private readonly logTemplate = _.template(logTemplate);
    private readonly trailerAttachmentCollection: TrailerAttachments;
    private readonly currentVehicle: Device;
    private readonly workflow: boolean;
    private readonly logout: boolean;
    private readonly changeVehicle: boolean;
    private readonly canEditDvir: boolean;
    private readonly canInspectDvir: boolean;
    private readonly canRepairDvir: boolean;
    private readonly canCertifyDvir: boolean;
    private readonly completeDvirIds: string[];
    private readonly deviceCache: BaseCache<Device | IDevice>;
    private dvirCollection: DVIRLog[];
    private buttonsBar: ButtonsBar | undefined;
    private incompleteDvirs: IDvirAudit[] = [];
    /** Set by DriveNative's per-trailer Start Inspection (MYG-149254); consumed on first render. */
    private trailerToOpen: string | undefined;

    constructor(options) {
        super(options, {
            title: "Asset Inspection",
            activeSection: "dvir",
            activeView: undefined,
            pageLevel: options.pageLevel || PageLevel.ONE
        });
        this.app = options.app;
        this.deviceCache = makeDeviceCache(this.app.store);
        this.trailerAttachmentCollection = new TrailerAttachments(this.app.api, this.app.store);
        this.listenTo(dispatcher, `${ DispatcherEvents.SyncDvir } ${ DispatcherEvents.DvirLogSet }`, () => {
            this.dvirCollection = this.app.dvirLogsService.getDvirLogs();
            this.trailerAttachmentCollection.fetch();
            this.renderImpl();
        });
        this.currentVehicle = new Device(getCurrentVehicle() || {});
        this.dvirCollection = this.app.dvirLogsService.getDvirLogs();

        this.workflow = options.workflow || false;
        this.logout = options.logout || false;
        this.changeVehicle = options.changeVehicle || false;
        this.completeDvirIds = options.completeDvirIds || [];
        this.trailerToOpen = this.workflow ? undefined : options.state?.trailerId;

        this.canEditDvir = this.app.securityService.can("useDvir");
        this.canInspectDvir = this.app.securityService.can("inspectDvir") && this.canEditDvir;
        this.canRepairDvir = this.app.securityService.can("repairDvir") && this.canEditDvir;
        this.canCertifyDvir = this.app.securityService.can("certifyDvir") && this.canEditDvir;
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click .create-dvir": this.create,
            "click .view-dvir": this.view,
            "click #skip-DVIR-button": this.skip
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    toggleSkipButton(enable: boolean) {
        if (this.buttonsBar) {
            if (enable) {
                this.buttonsBar.enableButtons();
            } else {
                this.buttonsBar.disableButtons();
            }
        }
    }

    createSkipButton() {
        if (!this.workflow) {
            return;
        }

        this.buttonsBar && this.buttonsBar.hide();
        this.buttonsBar = new ButtonsBar([{
            title: this.getString("Skip remainder"),
            id: `skip-DVIR-button`,
            className: "skip-dvir",
            clickHandler: () => this.skip()
        }], {
            isNightMode: this.app.store.getState().data.nightMode
        });

        this.buttonsBar.show();
    }

    isDvirCompleted(log?: DVIRLog) {
        return log && log.id && this.completeDvirIds.indexOf(log.id) > -1;
    }

    addToListIfDvirIncomplete(log: DVIRLog | null, isDevice?: boolean) {
        if (log && !this.isDvirCompleted(log)) {
            let asset = log.getResource();
            asset && this.incompleteDvirs.push({
                name: asset.name,
                id: asset.id,
                assetType: isDevice ? "vehicle" : "trailer"
            });
        }
    }

    getLogTemplate(log: DVIRLog, store: DriveStore) {
        let hasLastLog = !!log.id;
        let resource = log.getResource();
        const isTemp = resource instanceof Trailer ? resource._isTemp : false;
        const unrepairedDefects = log.getUnrepairedDefects();

        return this.logTemplate({
            log: log,
            hasLastLog,
            isCertified: log.isCertified(),
            isComplete: log.isComplete(this.app),
            isRepaired: log.isRepaired(),
            hasDefects: log.hasDefects(),
            hasBlockingUnrepairedDefect: log.hasBlockingUnrepairedDefect(store),
            needsReview: log.needsReview(store),
            driverRemark: htmlEscape(log.driverRemark || ""),
            logDateString: log.dateTime ? dayjs(dstCalculator.toLocalDateTime(log.dateTime)).format("lll") : null,
            minutesSince: hasLastLog && log.dateTime ? (getServerDate().getTime() - new Date(log.dateTime).getTime()) / (1000 * 60) : Infinity,
            canInspectDvir: this.canInspectDvir,
            // only want defects in status "NotRepaired" and not severity "Critical" as drivers without repair clearance can still set those to NotNecessary
            canRepairDvir: this.canRepairDvir || (this.canEditDvir && unrepairedDefects.some(defect => {
                const defectSeverity = (defect.defect as IDefect).severity;
                return defectSeverity !== "Critical";
            })),
            canCertifyDvir: this.canCertifyDvir,
            canSeeDvir: log.id || this.canInspectDvir,
            isDvirCompleted: this.isDvirCompleted(log),
            isTemp
        });
    }

    getTrailersLogTemplate(trailer: Trailer) {
        let lastTrailerLog = this.app.dvirLogsService.getLatestLog(this.dvirCollection, trailer.id);
        if (lastTrailerLog) {
            lastTrailerLog.trailer = trailer;
        }

        let reportLog = lastTrailerLog || new DVIRLog({
            id: "",
            trailer: trailer
        });
        this.addToListIfDvirIncomplete(reportLog, false);
        return this.getLogTemplate(reportLog, this.app.store);
    }

    render() {
        if (!this.currentVehicle || this.currentVehicle.id === "NoDeviceId") {
            this.app.router.navigate("main", true);
            return this;
        }

        this.createSkipButton();
        this.renderImpl();
        if (this.trailerToOpen) {
            let trailerId = this.trailerToOpen;
            this.trailerToOpen = undefined;
            this.openTrailer(trailerId);
        }
        return this;
    }

    renderImpl() {
        this.toggleSkipButton(false);
        this.incompleteDvirs = [];

        let vehicleLogsTemplate = "";
        let trailerLogsTemplate: any = [];
        let cachedDeviceData = this.deviceCache.get({ "id": this.currentVehicle.id })[0];

        let lastVehicleLog = this.app.dvirLogsService.getLatestLog(this.dvirCollection, this.currentVehicle.id);
        // Only enrich the log's device from the cache when data is available.
        // On a cold cache, lastVehicleLog retains the device reference from the store (valid per-sync data).
        let currentDevice = cachedDeviceData ? new Device(cachedDeviceData) : null;
        if (lastVehicleLog && currentDevice) {
            lastVehicleLog.device = currentDevice;
        }

        let reportLog = lastVehicleLog || new DVIRLog({
            id: "",
            device: currentDevice || this.currentVehicle
        });
        this.addToListIfDvirIncomplete(reportLog, true);
        vehicleLogsTemplate = this.getLogTemplate(reportLog, this.app.store);

        let trailers = this.trailerAttachmentCollection.getCurrentTrailers();
        trailers.sort((a, b) => a.name.localeCompare(b.name));
        trailerLogsTemplate = trailers.map(trailer => {
            return this.getTrailersLogTemplate(trailer);
        });

        void this.display(this.template({
            vehicleLogsTemplate,
            trailerLogsTemplate,
            // Not a Roadside Check page: the shared template still needs the full language view model.
            ...getHiddenRoadsideCheckLanguageViewModel(this.app)
        })).then(() => this.toggleSkipButton(true));
    }

    view(e) {
        let id = this.getButtonId(e, "data-id");
        this.workflow ? dispatcher.trigger(DispatcherEvents.CompleteWorkflowStep, id, false) : this.app.router.navigate(viewInspectionRoute(id), true);
    }

    create(e) {
        let resourceId = this.getButtonId(e, "data-resource-id");
        this.workflow ? dispatcher.trigger(DispatcherEvents.CompleteWorkflowStep, resourceId, true) : this.app.router.navigate(newInspectionRoute(resourceId), true);
    }

    /**
     * Opens one trailer's inspection the way its button on this list would: the
     * last log when there is one (so unrepaired defects carry over), otherwise a
     * new inspection. Leaves the list showing when that button would be
     * unavailable: the trailer isn't attached here yet (e.g. its native
     * attachment hasn't synced), is temporary, or the driver can't inspect it.
     */
    openTrailer(trailerId: string) {
        let trailer = this.trailerAttachmentCollection.findTrailer(trailerId);
        if (!trailer || trailer._isTemp) {
            return;
        }
        let lastLogId = this.app.dvirLogsService.getLatestLog(this.dvirCollection, trailerId)?.id;
        if (!lastLogId && !this.canInspectDvir) {
            return;
        }
        // Replace this entry with the plain list first, so Back from the inspection
        // returns to the list instead of re-opening the trailer.
        this.app.router.navigate("dvir/main", { trigger: false, replace: true });
        this.app.router.navigate(lastLogId ? viewInspectionRoute(lastLogId) : newInspectionRoute(trailerId), true);
    }

    skip() {
        if (this.incompleteDvirs && this.incompleteDvirs.length > 0) {
            this.incompleteDvirs.forEach(incompleteDvir => {
                let optionalParams = {
                    assetType: incompleteDvir.assetType,
                    assetName: incompleteDvir.name,
                    assetId: incompleteDvir.id
                };

                auditSkip(this.app.api, AuditType.DvirInspect, this.changeVehicle ? "Vehicle change" : this.logout ? "Logout" : "Login", optionalParams);
            });

            this.incompleteDvirs = [];
        }
        dispatcher.trigger(DispatcherEvents.CompleteWorkflowStep);
    }

    getButtonId(e, dataTag: string): string | undefined {
        // IE 11's currentTarget is not the button, so use `target` instead
        let target: HTMLElement = e.currentTarget && (e.currentTarget as HTMLElement).tagName === "BUTTON" ? e.currentTarget : e.target;
        return target.attributes.getNamedItem(dataTag)?.value;
    }

    remove() {
        this.buttonsBar && this.buttonsBar.hide();
        delete this.buttonsBar;
        super.remove();
        return this;
    }
}